import axios from 'axios';
import {
  Users, Calendar, Clock, Settings, CalendarPlus, HelpCircle, FileText, Database,
  MessageSquare, Archive, GraduationCap, Briefcase, ShieldCheck, ClipboardList,
  Building2, Activity, FileSearch, ScrollText, ClipboardCheck, History,
  BookOpen, Shield, SlidersHorizontal, QrCode, Package, Banknote, Network,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MENU_ITEMS = {
  '/admin/employees': { icon: Users, label: 'Employees' },
  '/admin/messages': { icon: MessageSquare, label: 'Messages' },
  '/admin/announcements': { icon: MessageSquare, label: 'Announcements' },
  // '/admin/upcoming-shifts' removed from the registry — merged into Event
  // Calendar (route still works, reachable via "+ Add Event" / event list).
  '/admin/archived-events': { icon: Archive, label: 'Archived Events' },
  '/admin/shifts': {
    icon: Calendar,
    label: 'Shift Calendar',
    children: [
      { path: '/admin/shifts/events', label: 'Event Calendar' },
      { path: '/admin/shifts/retail', label: 'Retail Calendar' },
      { path: '/admin/shifts/sites', label: 'Saved Sites' },
    ],
  },
  '/admin/clock-history': {
    icon: Clock,
    label: 'Clock History',
    children: [
      { path: '/admin/clock-history/retail', label: 'Retail Clock History' },
      { path: '/admin/clock-history/event', label: 'Event Clock History' },
      { path: '/admin/clock-history/payroll', label: 'Payroll Clock History' },
    ],
  },
  '/admin/claim-requests': { icon: ClipboardList, label: 'Claim Requests' },
  '/admin/departments/operations': { icon: Network, label: 'Operations' },
  '/admin/departments/control-room': { icon: Network, label: 'Control Room' },
  '/admin/departments/payroll': { icon: Network, label: 'Payroll' },
  '/admin/departments/hr-compliance': { icon: Network, label: 'HR & Compliance' },
  '/admin/departments/recruitment-onboarding': { icon: Network, label: 'Recruitment & Onboarding' },
  '/admin/departments/client-management': { icon: Network, label: 'Client Management' },
  '/admin/departments/quality-audit': { icon: Network, label: 'Quality & Audit' },
  '/admin/roles': { icon: Shield, label: 'Role Management' },
  '/admin/job-applications': { icon: Briefcase, label: 'Job Applications' },
  '/admin/compliance': { icon: ShieldCheck, label: 'HR & Compliance' },
  '/admin/compliance-queue': { icon: ClipboardList, label: 'Compliance Queue' },
  '/admin/document-review': { icon: FileSearch, label: 'Document Review' },
  '/admin/academic-breaks': { icon: GraduationCap, label: 'Academic Breaks' },
  '/admin/legal': { icon: FileText, label: 'Legal & Policies' },
  '/admin/policy-compliance': { icon: ClipboardCheck, label: 'Policy Compliance' },
  '/admin/mandatory-training': { icon: BookOpen, label: 'Mandatory Training' },
  '/admin/clients': { icon: Building2, label: 'Clients' },
  '/admin/staffing-requests': { icon: Briefcase, label: 'Staffing Requests' },
  '/admin/client-messages': { icon: MessageSquare, label: 'Client Messages' },
  '/admin/client-documents': { icon: FileText, label: 'Client Documents' },
  '/admin/app-login-activity': { icon: Activity, label: 'App Login Activity' },
  '/admin/shift-audit-log': { icon: ScrollText, label: 'Shift Audit Log' },
  '/admin/deletion-requests': { icon: ScrollText, label: 'Deletion Requests' },
  '/admin/onboarding-audit': { icon: History, label: 'Onboarding Audit' },
  '/admin/client-audit': { icon: History, label: 'Client Portal Audit' },
  '/admin/feedback': { icon: MessageSquare, label: 'Feedback' },
  '/admin/qr-assign': { icon: QrCode, label: 'QR Assign' },
  '/admin/kit': { icon: Package, label: 'Kit' },
  '/admin/payroll': {
    icon: Banknote,
    label: 'Payroll',
    children: [
      { path: '/admin/payroll', label: 'Payslips' },
      { path: '/admin/payroll/holiday', label: 'Holiday Entitlement' },
      { path: '/admin/payroll/breaks', label: 'Breaks' },
    ],
  },
  '/admin/duty-reports': { icon: ClipboardList, label: 'Duty Reports' },
  '/admin/faq': { icon: HelpCircle, label: 'FAQ' },
  '/admin/backup': { icon: Database, label: 'Backup' },
  '/admin/settings': { icon: Settings, label: 'Settings' },
  '/admin/help': { icon: HelpCircle, label: 'Help' },
  '/admin/menu-layout': { icon: SlidersHorizontal, label: 'Menu Layout' },
  // Supervisor-only personal pages (used by supervisorGroups below).
  '/supervisor/team-clock': { icon: Users, label: 'Team Clock In/Out' },
  '/supervisor/my-shifts': { icon: Calendar, label: 'My Shifts' },
  '/supervisor/available-shifts': { icon: CalendarPlus, label: 'Shifts Available' },
  '/supervisor/my-profile': { icon: Users, label: 'My Profile' },
  '/supervisor/my-history': { icon: History, label: 'My History' },
  '/supervisor/academic-breaks': { icon: GraduationCap, label: 'My Academic Breaks' },
  '/supervisor/team-academic-breaks': { icon: GraduationCap, label: 'Team Academic Breaks' },
  '/supervisor/legal': { icon: FileText, label: 'My Legal & Policies' },
  '/supervisor/mandatory-training': { icon: BookOpen, label: 'My Training' },
};

export const DEFAULT_GROUPS = [
  { id: 'shifts', title: 'Shifts', items: ['/admin/shifts', '/admin/archived-events', '/admin/clock-history', '/admin/claim-requests', '/admin/deletion-requests'] },
  { id: 'staff', title: 'Staff', items: ['/admin/employees', '/admin/messages', '/admin/announcements', '/admin/roles', '/admin/job-applications'] },
  { id: 'departments', title: 'Departments', items: ['/admin/departments/operations', '/admin/departments/control-room', '/admin/departments/payroll', '/admin/departments/hr-compliance', '/admin/departments/recruitment-onboarding', '/admin/departments/client-management', '/admin/departments/quality-audit'] },
  { id: 'payroll', title: 'Payroll', items: ['/admin/payroll'] },
  { id: 'compliance', title: 'Compliance', items: ['/admin/compliance', '/admin/compliance-queue', '/admin/document-review', '/admin/academic-breaks', '/admin/legal', '/admin/policy-compliance', '/admin/mandatory-training'] },
  { id: 'clients', title: 'Clients', items: ['/admin/clients', '/admin/staffing-requests', '/admin/client-messages', '/admin/client-documents'] },
  { id: 'audit', title: 'Audit', items: ['/admin/app-login-activity', '/admin/shift-audit-log', '/admin/onboarding-audit', '/admin/client-audit'] },
  { id: 'feedback', title: 'Feedback', items: ['/admin/feedback', '/admin/qr-assign', '/admin/kit'] },
  { id: 'system', title: 'System', items: ['/admin/duty-reports', '/admin/faq', '/admin/backup', '/admin/settings', '/admin/help', '/admin/menu-layout'] },
];

// Secondary-admin menu: same as admin but WITHOUT Clients/Audit, with the
// supervisor's personal pages added, and no Menu Layout editor.
export function supervisorGroups() {
  const groups = DEFAULT_GROUPS
    .filter((g) => g.id !== 'clients' && g.id !== 'audit' && g.id !== 'departments')
    .map((g) => ({ ...g, items: g.items.filter((p) => p !== '/admin/deletion-requests') }));
  const shifts = groups.find((g) => g.id === 'shifts');
  if (shifts) shifts.items.push('/supervisor/team-clock');
  groups.splice(1, 0, {
    id: 'myarea',
    title: 'My Area',
    items: [
      '/supervisor/my-shifts', '/supervisor/available-shifts', '/supervisor/my-profile',
      '/supervisor/my-history', '/supervisor/academic-breaks', '/supervisor/team-academic-breaks',
      '/supervisor/legal', '/supervisor/mandatory-training',
    ],
  });
  const sys = groups.find((g) => g.id === 'system');
  if (sys) sys.items = sys.items.filter((p) => p !== '/admin/menu-layout');
  return groups;
}

// Merge a saved layout with the registry: drop unknown paths, append any missing
// registry paths into their default group so new pages never disappear.
export function mergeLayout(saved) {
  if (!saved || !Array.isArray(saved) || saved.length === 0) {
    return DEFAULT_GROUPS.map((g) => ({ ...g, items: [...g.items] }));
  }
  const seen = new Set();
  const groups = saved.map((g) => ({
    id: g.id,
    title: g.title,
    items: (g.items || []).filter((p) => {
      if (!MENU_ITEMS[p] || seen.has(p)) return false;
      seen.add(p);
      return true;
    }),
  }));
  for (const def of DEFAULT_GROUPS) {
    const missing = def.items.filter((p) => !seen.has(p));
    if (missing.length === 0) continue;
    const target = groups.find((g) => g.id === def.id);
    if (target) target.items.push(...missing);
    else groups.push({ id: def.id, title: def.title, items: missing });
    missing.forEach((p) => seen.add(p));
  }
  return groups;
}

let cache = null;
let cacheUserId = null;

export async function loadMenuLayout(userId) {
  if (cache && cacheUserId === userId) return cache;
  try {
    const res = await axios.get(`${API}/admin/menu-layout`);
    cache = mergeLayout(res.data?.groups);
  } catch {
    cache = mergeLayout(null);
  }
  cacheUserId = userId;
  return cache;
}

export async function saveMenuLayout(groups, userId) {
  const res = await axios.put(`${API}/admin/menu-layout`, { groups });
  cache = mergeLayout(res.data.groups);
  cacheUserId = userId;
  window.dispatchEvent(new CustomEvent('admin-menu-updated', { detail: cache }));
  return cache;
}

export async function resetMenuLayout(userId) {
  await axios.delete(`${API}/admin/menu-layout`);
  cache = mergeLayout(null);
  cacheUserId = userId;
  window.dispatchEvent(new CustomEvent('admin-menu-updated', { detail: cache }));
  return cache;
}
